import { Link } from 'react-router-dom';
import { Button } from '@/components/ui/button';
import { DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuLabel, DropdownMenuSeparator, DropdownMenuTrigger } from '@/components/ui/dropdown-menu';
import { Avatar, AvatarFallback, AvatarImage } from '@/components/ui/avatar';
import { Badge } from '@/components/ui/badge';
import { User, LogOut, Shield, CreditCard, LifeBuoy, Package, Building2 } from 'lucide-react';
import { useAuth } from '@/contexts/AuthContext';
import { useAdminAccess } from '@/hooks/useAdminAccess';
import { NotificationBell } from '@/components/notifications/NotificationBell';
import { AuthenticatedMobileMenu } from '@/components/navigation/AuthenticatedMobileMenu';
export const AuthHeader = () => {
  const { user, signOut } = useAuth();
  const { isAdmin, isSuperAdmin } = useAdminAccess();
  const getInitials = (firstName?: string, lastName?: string) => {
    return `${firstName?.[0] || ''}${lastName?.[0] || ''}`.toUpperCase() || 'U';
  };
  if (!user) {
    return <div className="flex items-center gap-2">
        <Button asChild variant="ghostCopper" className="text-ivory">
          <Link to="/auth">Sign In</Link>
        </Button>
      </div>;
  }
  return <div className="flex items-center gap-4">
      <AuthenticatedMobileMenu />
      <NotificationBell />
      
      {/* Role Badge */}
      {isSuperAdmin && (
        <Badge className="bg-purple-500 text-white text-xs hidden md:flex">
          Super Admin
        </Badge>
      )}
      {isAdmin && !isSuperAdmin && (
        <Badge className="bg-red-500 text-white text-xs hidden md:flex">
          Admin
        </Badge>
      )}

      <DropdownMenu>
        <DropdownMenuTrigger asChild>
          <Button variant="ghost" className="relative h-8 w-8 rounded-full hover:bg-copper/10">
            <Avatar className="h-8 w-8 ring-2 ring-copper/40">
              <AvatarImage src={user.profile.avatar_url} alt={user.email} />
              <AvatarFallback className="bg-copper text-white">
                {getInitials(user.profile.first_name, user.profile.last_name)}
              </AvatarFallback>
            </Avatar>
          </Button>
        </DropdownMenuTrigger>
        <DropdownMenuContent className="w-56 bg-slate border border-deep-teal shadow-2xl shadow-black/50 z-50" align="end" forceMount side="bottom" sideOffset={5}>
          <DropdownMenuLabel className="font-normal">
            <div className="flex flex-col space-y-1">
              <p className="text-sm font-medium leading-none text-ivory">
                {user.profile.first_name} {user.profile.last_name}
              </p>
              <p className="text-xs leading-none text-ivory/60">
                {user.email}
              </p>
            </div>
          </DropdownMenuLabel>
          <DropdownMenuSeparator className="bg-deep-teal/60" />
          <DropdownMenuItem asChild className="text-ivory hover:text-copper hover:bg-copper/10 cursor-pointer">
            <Link to="/app/profile" className="flex items-center">
              <User className="mr-2 h-4 w-4" />
              Profile
            </Link>
          </DropdownMenuItem>
          <DropdownMenuItem asChild className="text-ivory hover:text-copper hover:bg-copper/10 cursor-pointer">
            <Link to="/app/support" className="flex items-center">
              <LifeBuoy className="mr-2 h-4 w-4" />
              Support
            </Link>
          </DropdownMenuItem>
          <DropdownMenuItem asChild className="text-ivory hover:text-copper hover:bg-copper/10 cursor-pointer">
            <Link to="/app/subscription" className="flex items-center">
              <CreditCard className="mr-2 h-4 w-4" />
              Subscription
            </Link>
          </DropdownMenuItem>
          <DropdownMenuItem asChild className="text-ivory hover:text-copper hover:bg-copper/10 cursor-pointer">
            <Link to="/app/sysco-catalog" className="flex items-center">
              <Package className="mr-2 h-4 w-4" />
              Sysco Catalog
            </Link>
          </DropdownMenuItem>
          
          {/* Admin Section */}
          {isAdmin && (
            <>
              <DropdownMenuSeparator className="bg-deep-teal/60" />
              <DropdownMenuLabel className="text-xs text-red-400 uppercase flex items-center gap-2">
                <Shield className="h-3 w-3" />
                Admin Portal
              </DropdownMenuLabel>
              <DropdownMenuItem asChild className="text-ivory hover:text-red-400 hover:bg-red-500/10 cursor-pointer">
                <Link to="/admin/dashboard" className="flex items-center">
                  <Shield className="mr-2 h-4 w-4" />
                  Dashboard
                </Link>
              </DropdownMenuItem>
            </>
          )}
          
          {/* Super Admin Section */}
          {isSuperAdmin && (
            <>
              <DropdownMenuSeparator className="bg-deep-teal/60" />
              <DropdownMenuLabel className="text-xs text-purple-400 uppercase flex items-center gap-2">
                <Building2 className="h-3 w-3" />
                Enterprise
              </DropdownMenuLabel>
              <DropdownMenuItem asChild className="text-ivory hover:text-purple-400 hover:bg-purple-500/10 cursor-pointer">
                <Link to="/admin/enterprise/overview" className="flex items-center">
                  <Building2 className="mr-2 h-4 w-4" />
                  Enterprise Console
                </Link>
              </DropdownMenuItem>
            </>
          )}
          
          <DropdownMenuSeparator className="bg-deep-teal/60" />
          <DropdownMenuItem className="text-red-400 hover:text-red-300 hover:bg-red-500/10 cursor-pointer" onClick={() => signOut()}>
            <LogOut className="mr-2 h-4 w-4" />
            Sign Out
          </DropdownMenuItem>
        </DropdownMenuContent>
      </DropdownMenu>
    </div>;
};